/**
 * Keeps an update log from growing forever.
 *
 * An append-only log is what makes concurrent writing safe, and it is also
 * what makes an hour of typing cost thousands of entries. Compaction writes
 * one entry encoding the whole state, then deletes the entries that entry
 * supersedes.
 *
 * **What this buys is round-trips, not bytes.** Measured against the Jazz 0.x
 * adapter, a log of 301 entries costs ~343 KB and 301 separate fetches; after
 * compaction it is ~188 KB and one fetch. The byte figure improves far less
 * than the entry count because a backend may not reclaim anything: on Jazz
 * 0.x the deletions are themselves operations in the list's own log, which
 * grows, and the superseded entries remain resident as unreferenced
 * CoValues. Compaction should therefore be understood as bounding *how much
 * a cold client has to ask for*, not as reclaiming storage.
 *
 * It runs in two phases:
 *
 * 1. **Snapshot.** Append the document's full state as one entry.
 * 2. **Prune** the entries that snapshot supersedes, after a grace window.
 *
 * **What makes this safe is `containedEntryIds`, not the grace window.**
 * `CollaborativeDocument.encodeSnapshot` reports exactly which entries the
 * snapshot it just encoded contains, and only those are ever removed — never
 * anything inferred from a timestamp or a list position, both of which are
 * unreliable across clients and delete an edit when they are wrong. Yjs state
 * is monotonic, so removing an entry from the log cannot un-apply it from a
 * peer that already had it.
 *
 * The grace window is a **heuristic**, and worth being honest about: it
 * measures time since this client issued the append, on this client's clock,
 * with no evidence that the snapshot reached anybody. It narrows the window
 * in which a peer materialising a document *from the log* could see the
 * deletions before the snapshot. It does nothing at all for a reader that was
 * not connected while it elapsed — a tab opened after the prune has had zero
 * grace by construction — and a forward clock step collapses it to nothing.
 */
import type { CollaborativeDocument } from "./CollaborativeDocument.js";
import type { UpdateLog, Unsubscribe } from "./update-log.js";

export interface CompactionPolicy {
  /**
   * Entry count at which a snapshot is written. Below it the log is small
   * enough that compacting costs more than it saves.
   */
  threshold?: number;
  /**
   * How long a snapshot must have been in the log before the entries it
   * supersedes are deleted. Sized for "every connected client has caught
   * up", not for correctness of the merge — a client that misses the window
   * still converges once the snapshot reaches it, it just reads a partial
   * document until then.
   */
  graceMs?: number;
  /** Clock, injectable so tests can drive the grace window. */
  now?: () => number;
  /**
   * Called when a snapshot or a prune fails. Compaction runs unattended off
   * log notifications and a timer, so without a hook every failure is an
   * unhandled rejection and a log that can never be compacted looks exactly
   * like one that does not need to be.
   */
  onError?: (error: unknown) => void;
}

const DEFAULT_THRESHOLD = 200;
const DEFAULT_GRACE_MS = 60_000;

function reportCompactionFailure(error: unknown): void {
  console.error("collab-text: compaction failed", error);
}

/** A snapshot written but not yet acted on. */
interface PendingSnapshot {
  supersededIds: string[];
  writtenAt: number;
}

export class Compactor {
  private readonly threshold: number;
  private readonly graceMs: number;
  private readonly now: () => number;
  private readonly onError: (error: unknown) => void;
  private pending: PendingSnapshot | null = null;
  private unsubscribe: Unsubscribe | null = null;
  private graceTimer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private stopped = false;

  constructor(
    private readonly document: CollaborativeDocument,
    private readonly log: UpdateLog,
    policy: CompactionPolicy = {},
  ) {
    this.threshold = policy.threshold ?? DEFAULT_THRESHOLD;
    this.graceMs = policy.graceMs ?? DEFAULT_GRACE_MS;
    this.now = policy.now ?? (() => Date.now());
    this.onError = policy.onError ?? reportCompactionFailure;
  }

  /**
   * Do whatever the log is due for — nothing, a snapshot, or a prune. Safe
   * to call as often as you like; it no-ops until something is due.
   *
   * Every client compacts its own logs. Two doing it at once produce two
   * snapshots and two prunes, and the second prune finds its entries already
   * gone, which the port defines as a no-op. Wasteful, never wrong, and
   * cheaper than electing a leader.
   */
  async run(): Promise<void> {
    // Re-entrancy guard: `run` is driven by log notifications, and its own
    // writes produce more of them.
    if (this.running) return;
    this.running = true;
    try {
      await this.step();
    } catch (error) {
      this.onError(error);
    } finally {
      this.running = false;
    }
  }

  /**
   * Compact on every log change. Also runs once immediately, so a log that
   * is already over the threshold does not wait for the next edit.
   */
  start(): void {
    if (this.unsubscribe) return;
    this.stopped = false;
    this.unsubscribe = this.log.subscribe(() => {
      void this.run();
    });
    void this.run();
  }

  /**
   * Stop watching the log, cancel the grace timer, and abandon any pending
   * snapshot. Nothing an in-flight `step` does afterwards takes effect.
   */
  stop(): void {
    this.stopped = true;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.clearGrace();
    this.pending = null;
  }

  /**
   * Wake up when the grace window has elapsed.
   *
   * Phase 2 cannot be left to the next log notification. The one the
   * snapshot's own append produces is swallowed by the re-entrancy guard, and
   * the shape that follows — type past the threshold, stop, close the tab —
   * produces no further notifications at all. Without a timer that snapshot
   * is never pruned, the next session appends another on top of it, and
   * compaction ends up making the log *larger* than leaving it alone.
   */
  private armGrace(): void {
    if (this.graceTimer !== null || this.stopped) return;
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      void this.run();
    }, this.graceMs);
  }

  private clearGrace(): void {
    if (this.graceTimer === null) return;
    clearTimeout(this.graceTimer);
    this.graceTimer = null;
  }

  private async step(): Promise<void> {
    if (this.pending) {
      if (this.now() - this.pending.writtenAt < this.graceMs) {
        this.armGrace();
        return;
      }
      const doomed = this.pending.supersededIds;
      try {
        await this.log.prune(doomed);
      } catch (error) {
        // Keep the ids rather than abandon them: dropping `pending` before
        // the prune succeeded left the superseded entries in the log with
        // nothing that would ever remove them.
        this.onError(error);
        this.pending = { supersededIds: doomed, writtenAt: this.now() };
        this.armGrace();
        return;
      }
      this.pending = null;
      return;
    }

    if (this.log.getEntries().length < this.threshold) return;

    const { update, containedEntryIds } = this.document.encodeSnapshot();
    const id = await this.log.append(update, { isSnapshot: true });
    // A reader cannot compact. Leaving `pending` null means it will keep
    // trying, which costs one refused append per notification and is the
    // right outcome: someone with write access will do it.
    if (id === null) return;
    // `stop()` may have run while the append was in flight; it means this
    // compactor is finished, so do not resurrect a pending snapshot behind it.
    if (this.stopped) return;
    this.pending = {
      supersededIds: containedEntryIds,
      writtenAt: this.now(),
    };
    this.armGrace();
  }
}
